import { useState } from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { EntryRequestActions } from "@/features/entry-requests"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

export type StaffEntryRequest = {
  id: number
  requesterName: string
  requestedAt: string
  /** A duration, not a timestamp — see `EntryAskPanel` for the same clock-skew reasoning. */
  expiresInSeconds: number
  approveHref: string
  rejectHref: string
}

type Props = {
  requests: StaffEntryRequest[]
  className?: string
  /**
   * Overridable because the table screen renders this twice — in the desktop
   * context rail and in the drawer the phone opens — and the same selector
   * resolving to two nodes is a broken query, not a duplicate view.
   */
  testId?: string
}

function minutesWaiting(requestedAt: string): number {
  const elapsedMs = Date.now() - new Date(requestedAt).getTime()
  return Math.max(0, Math.floor(elapsedMs / 60_000))
}

/**
 * Someone standing at the table asking to be let in. Shown above the invite
 * panel on purpose: a person who cannot sit down outranks the QR the table
 * does not need yet, the same ranking `table-status.ts` gives `entry_requested`
 * on the floor overview. Shared markup for the floor drawer and the table's
 * own summary drawer — `Staff::TablesWorkspaceSerializer` already sends the
 * same rows to both.
 */
export function EntryRequestPanel({ requests, className, testId = "entry-request-panel" }: Props) {
  // A row staff just rejected drops out of the next `entryRequests` payload
  // immediately: the server only ever lists `pending_active` requests, so a
  // resolved one simply stops arriving rather than changing status in place.
  // The undo lives here, in local state keyed by id, for as long as this
  // screen stays mounted — it is the one thing `Staff::Location::EntryRequestsController#clear`
  // is for, and this panel cannot re-surface a row the server no longer sends
  // once the waiter navigates away.
  const [justRejected, setJustRejected] = useState<Record<number, string>>({})

  if (requests.length === 0 && Object.keys(justRejected).length === 0) return null

  const undoReject = (id: number) => {
    router.patch(
      `/entry_requests/${id}/clear`,
      {},
      {
        preserveScroll: true,
        onSuccess: () =>
          setJustRejected((current) => {
            const next = { ...current }
            delete next[id]
            return next
          }),
      }
    )
  }

  return (
    <section
      className={cn("space-y-2 rounded-xl border p-3", className)}
      data-testid={testId}
    >
      <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t("ui.react.features.staff.entry_request_panel.title")}
      </h5>

      <ul className="space-y-2">
        {requests.map((request) => (
          <li
            key={request.id}
            className="space-y-1.5 rounded-lg border bg-card p-2.5"
            data-testid={`entry-request-row-${request.id}`}
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{request.requesterName}</p>
              <p className="text-xs tabular-nums text-muted-foreground">
                {t("ui.react.features.staff.entry_request_panel.minutes_waiting", {
                  count: minutesWaiting(request.requestedAt),
                })}
              </p>
            </div>
            <EntryRequestActions
              entryRequestId={request.id}
              approveHref={request.approveHref}
              rejectHref={request.rejectHref}
              approveLabel={t("ui.react.features.entry_requests.staff.approve")}
              onResolved={(action) => {
                if (action !== "reject") return
                setJustRejected((current) => ({ ...current, [request.id]: request.requesterName }))
              }}
            />
            <p className="text-xs text-muted-foreground">
              {t("ui.react.features.entry_requests.staff.approve_hint")}
            </p>
          </li>
        ))}

        {Object.entries(justRejected).map(([id, requesterName]) => (
          <li
            key={id}
            className="flex items-center justify-between gap-2 rounded-lg border border-dashed bg-muted/30 p-2.5"
            data-testid={`entry-request-rejected-${id}`}
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-muted-foreground">{requesterName}</p>
              <p className="text-xs text-muted-foreground">
                {t("ui.react.features.staff.entry_request_panel.rejected")}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => undoReject(Number(id))}
              data-testid={`entry-request-undo-${id}`}
            >
              {t("ui.react.features.staff.entry_request_panel.undo")}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  )
}
